<script lang="ts">
  /**
   * One flight: the 3D track, the barogram, the numbers, and the IGC download.
   *
   * The map and the barogram share one `cursor` — the index into the track — so scrubbing the
   * graph moves the aircraft and vice versa. That single shared number is the whole interaction
   * model, and it is why neither component owns the playback state.
   */
  import Badges from './Badges.svelte';
  import Barogram from './Barogram.svelte';
  import Gafor from './Gafor.svelte';
  import FlightMap from './FlightMap.svelte';
  import LoopingPlane from './LoopingPlane.svelte';
  import { api } from './api.ts';
  import { describeSky } from './sky.ts';
  import type {
    Aircraft,
    CloudProfile,
    Flight,
    FlightWeather,
    GaforOutlook,
    MapConfig,
    NotamIndex,
    TerrainProfile,
    TrackFeature,
  } from './api.ts';
  import {
    duration,
    durationLong,
    climb,
    km,
    kmh,
    localDate,
    localTime,
    metres,
    number,
    percent,
    place,
    utcTime,
  } from './format.ts';

  interface Props {
    id: string;
    aircraft: Aircraft[];
    onBack: () => void;
  }

  const LAUNCH_LABEL: Record<string, string> = {
    winch: 'Windenstart',
    aerotow: 'F-Schlepp',
    self_launch: 'Eigenstart',
  };

  let { id, aircraft, onBack }: Props = $props();

  /**
   * The weather over the flight, for the sky and for a line of text.
   *
   * Fetched separately from the flight and allowed to fail quietly: it reaches a third-party
   * forecast host through the daemon, and a page that could not render because a weather
   * service was down would be a poor trade for a nicer gradient.
   */
  let weather = $state<FlightWeather | null>(null);
  let gafor = $state<GaforOutlook | null>(null);
  let gaforLoading = $state(false);
  let notams = $state<NotamIndex | null>(null);
  let terrain = $state<TerrainProfile | null>(null);
  let clouds = $state<CloudProfile | null>(null);

  $effect(() => {
    const controller = new AbortController();
    weather = null;
    api
      .weather(id, controller.signal)
      .then((w) => {
        weather = w.source === 'unavailable' ? null : w;
      })
      .catch(() => {
        /* Decoration. A clear default sky is a fine fallback. */
      });
    return () => controller.abort();
  });

  /**
   * The cloud base over the flight, for the barogram.
   *
   * Same posture as the weather and the terrain: a chart is complete without it, and a page
   * must not fail because a forecast host did not answer.
   */
  $effect(() => {
    const controller = new AbortController();
    clouds = null;
    api
      .clouds(id, controller.signal)
      .then((c) => {
        clouds = c.available ? c : null;
      })
      .catch(() => {});
    return () => controller.abort();
  });

  $effect(() => {
    const controller = new AbortController();
    terrain = null;
    api
      .terrain(id, controller.signal)
      .then((t) => {
        terrain = t.available ? t : null;
      })
      .catch(() => {
        /* The barogram is complete without it. */
      });
    return () => controller.abort();
  });

  let flight = $state<Flight | null>(null);
  let track = $state<TrackFeature | null>(null);
  let mapConfig = $state<MapConfig | null>(null);
  let error = $state<string | null>(null);

  let cursor = $state(0);
  let playing = $state(false);
  let speed = $state(60);
  let basemap = $state<'satellite' | 'vector'>('satellite');
  let showAirspace = $state(false);
  /**
   * Cockpit view — the camera in the aircraft rather than above it.
   *
   * Turning it on starts the replay, because a cockpit view of a stationary aircraft is a
   * photograph. Turning it off leaves the replay running and returns the camera to the
   * overview, which is the other half of the same expectation.
   */
  let cockpit = $state(false);

  /**
   * How often an open flight is re-read.
   *
   * The OGN beacon interval for an aircraft in the air is a few seconds, and the daemon writes
   * every fix, so ten seconds is fresh enough to watch and light enough that a page left open
   * all afternoon is not a load on anything.
   */
  const LIVE_POLL_MS = 10_000;

  /** The outlook only moves with the aircraft and the model; five minutes is generous. */
  const GAFOR_POLL_MS = 5 * 60_000;

  /**
   * Whether the view pins itself to the newest fix.
   *
   * On by default for a flight in the air, because "where is it now" is the only reason to open
   * that page while it is happening — and it starts at the *right-hand* end of the barogram
   * rather than at the take-off, which is where a replay starts and where a live flight
   * emphatically should not.
   *
   * Any deliberate interaction turns it off: pressing play, or touching the barogram. Following
   * the aircraft while somebody is trying to scrub back to a thermal is not helpfulness.
   */
  let following = $state(true);

  /**
   * The last cursor position this component set on its own.
   *
   * How "the reader took over" is detected. A pointer handler on the barogram was the obvious
   * way and the wrong one — it needs an ARIA role it has no business having, and it misses the
   * keyboard entirely. Comparing against the last value *we* wrote catches every route to a
   * moved cursor, including ones that do not exist yet.
   */
  let pinnedCursor = -1;

  function pinToEnd(t: TrackFeature): void {
    cursor = Math.max(0, t.properties.times.length - 1);
    pinnedCursor = cursor;
  }

  const isOpen = $derived(flight?.status === 'open');

  // Somebody scrubbed. Stop dragging them back to the present.
  $effect(() => {
    if (cursor !== pinnedCursor) following = false;
  });

  const registration = $derived(
    aircraft.find((a) => a.id === flight?.aircraftId)?.registration ?? flight?.aircraftId ?? '',
  );
  const model = $derived(aircraft.find((a) => a.id === flight?.aircraftId)?.model ?? null);

  $effect(() => {
    const controller = new AbortController();
    error = null;
    flight = null;
    track = null;
    cursor = 0;

    Promise.all([
      api.flight(id, controller.signal),
      api.track(id, controller.signal),
      api.mapConfig(controller.signal),
    ])
      .then(([f, t, c]) => {
        flight = f;
        track = t;
        mapConfig = c;
        following = f.status === 'open';
        if (following) pinToEnd(t);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        error = err instanceof Error ? err.message : String(err);
      });

    return () => controller.abort();
  });

  /**
   * A flight in the air keeps arriving, so keep reading it.
   *
   * Gated on a `$derived` boolean rather than on `flight?.status` directly: the poll assigns to
   * `flight`, and an effect that read the object itself would tear down and restart its own
   * timer on every tick. The boolean only changes when the flight actually lands.
   *
   * A failed poll is deliberately silent. The next one is ten seconds away, and turning a
   * moment of Wi-Fi into a red error banner over a working page helps nobody.
   */
  $effect(() => {
    if (!isOpen) return;
    const controller = new AbortController();

    const timer = setInterval(() => {
      void Promise.all([api.flight(id, controller.signal), api.track(id, controller.signal)])
        .then(([f, t]) => {
          flight = f;
          track = t;
          if (following) pinToEnd(t);
        })
        .catch(() => {});
    }, LIVE_POLL_MS);

    return () => {
      clearInterval(timer);
      controller.abort();
    };
  });

  /**
   * The flyability outlook, while the aircraft is in the air.
   *
   * Refreshed on the same clock as the position but far less often — the daemon caches the
   * model answer for a quarter of an hour anyway, so asking more would only move bytes.
   */
  $effect(() => {
    if (!isOpen) return;
    const controller = new AbortController();

    const load = () => {
      gaforLoading = gafor === null;
      api
        .gafor(id, controller.signal)
        .then((g) => {
          gafor = g;
          gaforLoading = false;
        })
        .catch(() => {
          gaforLoading = false;
        });
    };

    load();
    const timer = setInterval(load, GAFOR_POLL_MS);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  });

  /**
   * NOTAMs near the aircraft.
   *
   * Also fetched for a landed flight — a NOTAM around the field it landed at is still a fact
   * about that field, and the layer only appears at all when the daemon has credentials.
   */
  $effect(() => {
    const controller = new AbortController();
    notams = null;
    api
      .notams(id, controller.signal)
      .then((n) => {
        notams = n;
      })
      .catch(() => {
        /* Overlay. */
      });
    return () => controller.abort();
  });

  /**
   * Playback.
   *
   * Driven by requestAnimationFrame against real elapsed time rather than by a fixed step per
   * frame: a 120 Hz phone and a throttled background tab would otherwise replay the same
   * flight at different speeds.
   */
  $effect(() => {
    if (!playing || track === null) return;

    const times = track.properties.times;
    const last = times.length - 1;
    let frame = 0;
    let previous = performance.now();

    const tick = (now: number) => {
      const elapsed = (now - previous) / 1000;
      previous = now;

      const target = (times[cursor] ?? 0) + elapsed * speed;
      let next = cursor;
      while (next < last && (times[next] ?? 0) < target) next += 1;

      if (next >= last) {
        cursor = last;
        playing = false;
        return;
      }
      cursor = next;
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  });

  function togglePlay(): void {
    if (track === null) return;
    // Replaying from the beginning and being dragged back to the present every ten seconds are
    // mutually exclusive intentions.
    following = false;
    // Restarting from the end is what a play button is expected to do there.
    if (cursor >= track.properties.times.length - 1) cursor = 0;
    playing = !playing;
  }
</script>

<div class="view">
  <header>
    <button class="back" onclick={onBack} type="button" aria-label="Zurück zum Flugbuch">←</button>
    <div class="title">
      {#if flight !== null}
        <h1>{registration} <span class="faint">{localDate(flight.takeoffT)}</span></h1>
        <!--
          Two facts, and on a phone two lines.

          As one flowing sentence it broke wherever it ran out of room: "· 3" ended one line and
          "h 03 min" began the next, which reads as a rendering fault rather than as a duration.
          Splitting where the meaning splits — where it flew, then when — puts the break under
          our control, and the separator is drawn by CSS so it can disappear with the join.
        -->
        <p class="sub muted">
          <!-- Both, always, and never collapsed to one when they happen to match. A logbook
               has a Start column and a Landung column and they are both filled in even for a
               local flight; showing a single name left it genuinely unclear which one it was,
               and for a cross-country the second one is the whole story. -->
          <span class="where">
            {place(flight.takeoffPlace, flight.takeoffPlaceCode)}
            →
            {#if flight.status === 'open'}
              <span class="running">unterwegs</span>
            {:else}
              {place(flight.landingPlace, flight.landingPlaceCode)}
            {/if}
          </span>
          <span class="when">
            {#if flight.status === 'open'}
              <!-- An open flight has no landing time, and rendering the closed form anyway gave
                   "10:02–– · –", which reads as a fault rather than as a flight in progress. -->
              <span class="seg">seit {localTime(flight.takeoffT)}</span>
              <span class="seg">· <span class="running">läuft</span></span>
            {:else}
              <span class="seg">{localTime(flight.takeoffT)}–{localTime(flight.landingT)}</span>
              <span class="seg">· {durationLong(flight.durationS)}</span>
            {/if}
          </span>
        </p>
      {:else}
        <h1 class="faint">Flug {id}</h1>
      {/if}
    </div>
    {#if flight?.igcPath != null}
      <a class="igc" href={api.igcUrl(id)} download>IGC</a>
    {/if}
  </header>

  {#snippet gaforChrome()}
    <Gafor outlook={gafor} loading={gaforLoading} />
  {/snippet}

  {#if error !== null}
    <p class="state bad">Flug nicht ladbar: {error}</p>
  {:else if flight === null || track === null || mapConfig === null}
    <p class="state"><LoopingPlane width={132} seconds={4} label="Lädt" /></p>
  {:else}
    <div class="map">
      <FlightMap
        {track}
        config={mapConfig}
        {cursor}
        bind:basemap
        bind:showAirspace
        weather={weather ?? {}}
        takeoffPlace={flight.takeoffPlace}
        landingPlace={flight.status === 'open' ? null : flight.landingPlace}
        chrome={flight.status === 'open' ? gaforChrome : undefined}
        offerRadar={flight.status === 'open'}
        offerThermals={flight.soaring !== false}
        {notams}
        bind:cockpit
      />
    </div>

    <div class="controls">
      <button class="play" onclick={togglePlay} type="button" aria-label={playing ? 'Pause' : 'Abspielen'}>
        {playing ? '⏸' : '▶'}
      </button>
      <label class="speed">
        <span class="faint">Tempo</span>
        <select bind:value={speed}>
          <option value={20}>20×</option>
          <option value={60}>60×</option>
          <option value={200}>200×</option>
          <option value={600}>600×</option>
        </select>
      </label>
      <button
        class="cockpit"
        class:on={cockpit}
        type="button"
        aria-pressed={cockpit}
        title="Kamera ins Flugzeug — Blick in Flugrichtung"
        onclick={() => {
          cockpit = !cockpit;
          // A cockpit view of an aircraft that is not moving is a photograph.
          if (cockpit && !playing) togglePlay();
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M2 12h20M12 4c2.5 2.2 4 4.9 4 8s-1.5 5.8-4 8c-2.5-2.2-4-4.9-4-8s1.5-5.8 4-8z" />
          <circle cx="12" cy="12" r="9" />
        </svg>
        Cockpit
      </button>
      {#if flight.status === 'open'}
        <button
          class="follow"
          class:on={following}
          type="button"
          aria-pressed={following}
          title="Springt bei jeder neuen Position ans rechte Ende"
          onclick={() => {
            following = !following;
            if (following && track !== null) pinToEnd(track);
          }}
        >
          <span class="dot" aria-hidden="true"></span>
          {following ? 'folgt live' : 'live folgen'}
        </button>
      {/if}
      <span class="progress num faint">
        {localTime(track.properties.times[cursor] ?? null)} /
        {localTime(track.properties.times[track.properties.times.length - 1] ?? null)}
      </span>
    </div>

    <section class="baro card">
      <Barogram {track} bind:cursor terrain={terrain ?? undefined} clouds={clouds ?? undefined} />
    </section>

    {#if flight.score !== null}
      <section class="scores">
        <div class="score-card card">
          <h2>OLC-Plus</h2>
          <p class="big num">{number(flight.score.olcPlusPoints, 1)} <span class="unit">P</span></p>
          <dl>
            <div><dt>Freie Strecke</dt><dd class="num">{km(flight.score.freeDistanceKm)}</dd></div>
            <div>
              <dt>FAI-Dreieck</dt>
              <dd class="num">
                {flight.score.faiValid ? km(flight.score.faiTriangleKm) : 'nicht gewertet'}
              </dd>
            </div>
            <div><dt>Index {aircraft.find((a) => a.id === flight?.aircraftId)?.olcIndex ?? '–'}</dt><dd class="num">{number(flight.score.olcPlusScore, 2)}</dd></div>
          </dl>
        </div>

        <div class="score-card card">
          <h2>WeGlide</h2>
          <p class="big num">{number(flight.score.weglidePoints, 1)} <span class="unit">P</span></p>
          <dl>
            <div>
              <dt>Flächenbonus</dt>
              <dd class="num">
                {#if flight.score.weglideAreaBonusSource === 'fai'}
                  FAI-Dreieck
                {:else if flight.score.weglideAreaBonusSource === 'out_and_return'}
                  Zielrückflug
                {:else}
                  keiner
                {/if}
              </dd>
            </div>
            <div><dt>Zielrückflug</dt><dd class="num">{km(flight.score.outAndReturnKm)}</dd></div>
            <div><dt>Index {aircraft.find((a) => a.id === flight?.aircraftId)?.dmstIndex ?? '–'}</dt><dd class="num">{number(flight.score.weglideScore, 2)}</dd></div>
          </dl>
        </div>
      </section>
    {/if}

    <!--
      Out of the scoring block, where these three used to live.

      The nesting was wrong from the start and only became visible when the ultralight stopped
      being scored: the weather that day, the launch method and the badges are all facts about
      the *flight*, and none of them has anything to do with whether an OLC figure exists. The
      first flight without a score lost all three at once, including the Eigenstart that is the
      one launch verdict this project can be certain of.
    -->
    {#if weather !== null && describeSky(weather) !== null}
      <p class="weather">
        <span class="tag">{describeSky(weather)}</span>
        <span class="why">
          {#if weather.temperatureC !== null}{Math.round(weather.temperatureC)} °C{/if}
          {#if weather.windSpeedKmh !== null}
            · Wind {Math.round(weather.windSpeedKmh)} km/h
            {#if weather.windDirectionDeg !== null}aus {Math.round(weather.windDirectionDeg)}°{/if}
          {/if}
          {#if weather.cloudMid !== null && weather.cloudMid > 40}
            · mittelhohe Bewölkung {Math.round(weather.cloudMid)} %
          {/if}
          — {weather.source === 'forecast' ? 'ICON-D2' : 'ECMWF IFS'}, Stunde
          {String(weather.hourUtc).padStart(2, '0')}:00 UTC
        </span>
      </p>
    {/if}

    {#if flight.launch != null && flight.launch.type !== 'unknown'}
      <p class="launch">
        <span class="tag">{LAUNCH_LABEL[flight.launch.type]}</span>
        <span class="why">{flight.launch.reason}</span>
        {#if flight.launch.confidence !== 'high'}
          <span class="soft">— unsicher</span>
        {/if}
      </p>
    {/if}

    {#if flight.badges != null}
      <Badges assessment={flight.badges} />
    {/if}

    {#if flight.score !== null}
      {#if flight.score.notes !== null}
        <!-- The scorer writes a note when the numbers need one — most importantly that a flight
             is synthetic. A demo flight that sits in the logbook looking exactly like a real one
             is a small lie that will eventually be believed, and it was invisible until this
             existed: the note was in the database and in the API and nowhere on the page. -->
        <p class="note flagged">{flight.score.notes}</p>
      {/if}
      {#if !flight.score.altitudeRuleOk}
        <p class="note">
          Die 1000-m-Höhenregel hat gegriffen: ohne sie wären es
          {km(flight.score.freeDistanceUnconstrainedKm)} freie Strecke gewesen.
        </p>
      {/if}
      {#if !flight.score.faiValid && flight.score.bestTriangleKm > 0}
        <p class="note">
          Größtes Dreieck im Flug: {km(flight.score.bestTriangleKm)} — nicht gewertet
          {#if flight.score.faiClosureM === null}
            (Schluss nicht innerhalb 1 km)
          {:else if !flight.score.bestTriangleValid}
            (Schenkelregel nicht erfüllt)
          {/if}.
        </p>
      {/if}
    {/if}

    <section class="facts card">
      <h2>Kennzahlen</h2>
      <dl>
        <div><dt>Muster</dt><dd>{model ?? '–'}</dd></div>
        <div><dt>Dauer</dt><dd class="num">{duration(flight.durationS)}</dd></div>
        <div><dt>Startplatz</dt><dd>{place(flight.takeoffPlace, flight.takeoffPlaceCode)}</dd></div>
        <div>
          <dt>Landeplatz</dt>
          <dd>
            {flight.status === 'open'
              ? 'noch in der Luft'
              : place(flight.landingPlace, flight.landingPlaceCode)}
          </dd>
        </div>
        <div><dt>Start (UTC)</dt><dd class="num">{utcTime(flight.takeoffT)}</dd></div>
        <div><dt>Landung (UTC)</dt><dd class="num">{utcTime(flight.landingT)}</dd></div>
        <div><dt>Max. Höhe</dt><dd class="num">{metres(flight.metrics?.maxAltM)}</dd></div>
        {#if flight.soaring !== false}
          <!-- Only for something that soars. The best climb of a powered aircraft is a
               statement about the throttle, not about the air, and putting it next to a
               glider's 4,2 m/s invites a comparison between two unrelated things. -->
          <div><dt>Bestes Steigen</dt><dd class="num">{climb(flight.metrics?.maxClimbMs)}</dd></div>
        {/if}
        <div><dt>Tracklänge</dt><dd class="num">{km(flight.metrics?.trackLengthKm)}</dd></div>
        <div><dt>Schnitt</dt><dd class="num">{kmh(flight.score?.avgSpeedKmh)}</dd></div>
        <div><dt>Fixes</dt><dd class="num">{flight.metrics?.fixCount ?? '–'}</dd></div>
        <div><dt>Quelle</dt><dd>{flight.source}</dd></div>
      </dl>
    </section>

    <!--
      Coverage is the first thing to look at when this scoring disagrees with an on-board
      logger, so it is stated plainly rather than buried in a tooltip.
    -->
    <section class="facts card">
      <h2>Empfangsqualität</h2>
      <dl>
        <div>
          <dt>Abdeckung</dt>
          <dd class="num" class:warn={(flight.metrics?.coverageRatio ?? 1) < 0.9}>
            {percent(flight.metrics?.coverageRatio)}
          </dd>
        </div>
        <div><dt>Lücken</dt><dd class="num">{flight.metrics?.gapCount ?? '–'}</dd></div>
        <div><dt>Längste Lücke</dt><dd class="num">{flight.metrics?.longestGapS ?? '–'} s</dd></div>
        <div>
          <dt>Landung</dt>
          <dd>{flight.landingInferred ? 'aus Signalverlust geschlossen' : 'beobachtet'}</dd>
        </div>
      </dl>
      {#if (flight.metrics?.coverageRatio ?? 1) < 0.9}
        <p class="note">
          Mehr als 10 % des Fluges ohne Empfang. Die Streckenwertung ist dadurch eher zu kurz
          als zu lang — eine Lücke über einem Wendepunkt kostet direkt Kilometer.
        </p>
      {/if}
    </section>

    <p class="disclaimer">
      Aus Netzwerkdaten abgeleitet — <strong>kein zugelassener Flugdatenschreiber</strong>. Die
      IGC-Datei hat keinen gültigen G-Record und ist für FAI-Abzeichen, DMSt oder Wettbewerbe
      nicht verwendbar.
    </p>
  {/if}
</div>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  header {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
  }

  .back {
    width: 44px;
    font-size: 1.3rem;
    color: var(--text-dim);
    border: 1px solid var(--line);
    border-radius: var(--radius-sm);
    flex: none;
  }

  .title {
    flex: 1;
    min-width: 0;
  }
  h1 {
    margin: 0;
    font-size: 1.15rem;
    line-height: 1.3;
  }
  .sub {
    margin: 0.15rem 0 0;
    font-size: 0.85rem;
  }
  .seg {
    display: inline-block;
    white-space: nowrap;
  }
  .when::before {
    content: ' · ';
  }
  /* Phone: where, then when, on lines of their own — and no orphaned separator to start the
     second one with. */
  @media (max-width: 559px) {
    .where,
    .when {
      display: block;
    }
    .when::before {
      content: none;
    }
  }

  .igc {
    flex: none;
    padding: 0 0.9rem;
    line-height: 44px;
    border-radius: var(--radius-sm);
    border: 1px solid color-mix(in srgb, var(--accent) 40%, transparent);
    background: var(--accent-soft);
    color: var(--accent);
    font-weight: 700;
    font-size: 0.85rem;
  }
  .igc:hover {
    text-decoration: none;
    border-color: var(--accent);
  }

  .state {
    padding: 3rem 1rem;
    text-align: center;
    color: var(--text-dim);
  }
  .state.bad {
    color: var(--bad);
  }

  .map {
    height: 52vh;
    /*
     * `svh` on a phone, where `vh` is a promise the browser does not keep.
     *
     * `vh` is the *large* viewport — the page as it would be with the address bar hidden — so
     * 52vh is more than 52 % of what is actually on the glass, and the bottom of the map sits
     * under the toolbar until you scroll. `svh` is the small viewport and is honest about it.
     * Not `dvh`: that one changes as the toolbar slides away, and MapLibre would resize its
     * canvas on every one of those frames.
     */
    height: 52svh;
    min-height: 300px;
    border-radius: var(--radius);
    overflow: hidden;
    border: 1px solid var(--line);
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    /* Four controls and a clock do not fit across a phone. Wrapping puts the clock on its own
       line rather than breaking it in half, which is what it did: "12:00 /" over "15:05". */
    flex-wrap: wrap;
    row-gap: 0.5rem;
  }
  .play {
    width: 44px;
    border-radius: 50%;
    border: 1px solid var(--line);
    background: var(--bg-raised);
    font-size: 1rem;
  }
  .speed {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
  }
  .speed select {
    font: inherit;
    color: inherit;
    background: var(--bg-raised);
    border: 1px solid var(--line);
    border-radius: var(--radius-sm);
    padding: 0.3rem 0.4rem;
  }
  .cockpit {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font: inherit;
    font-size: 0.8rem;
    color: var(--text-dim);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.32rem 0.7rem;
    cursor: pointer;
  }
  .cockpit svg {
    width: 0.95rem;
    height: 0.95rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
  }
  .cockpit.on {
    color: #04121a;
    background: var(--accent);
    border-color: var(--accent);
    font-weight: 600;
  }

  .follow {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    font: inherit;
    font-size: 0.8rem;
    color: var(--text-dim);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.32rem 0.7rem;
    cursor: pointer;
  }
  .follow .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.4;
  }
  .follow.on {
    color: #7ee2a8;
    border-color: rgba(126, 226, 168, 0.35);
    background: rgba(126, 226, 168, 0.1);
  }
  .follow.on .dot {
    opacity: 1;
    /* The same slow pulse as the "in der Luft" pill, so the two read as one state. */
    animation: follow-pulse 2s ease-in-out infinite;
  }
  @keyframes follow-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .follow.on .dot {
      animation: none;
    }
  }

  .progress {
    margin-left: auto;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  .baro {
    padding: 0.7rem;
  }

  .scores {
    display: grid;
    gap: 0.6rem;
    grid-template-columns: 1fr;
  }
  @media (min-width: 640px) {
    .scores {
      grid-template-columns: 1fr 1fr;
    }
  }

  .score-card,
  .facts {
    padding: 0.9rem 1rem;
  }

  h2 {
    margin: 0 0 0.4rem;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-faint);
  }

  .big {
    margin: 0 0 0.5rem;
    font-size: 1.9rem;
    font-weight: 700;
    line-height: 1;
    color: var(--accent);
  }
  .unit {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-dim);
  }

  dl {
    margin: 0;
    display: grid;
    gap: 0.15rem 1rem;
    grid-template-columns: 1fr;
    font-size: 0.88rem;
  }
  dl > div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.15rem 0;
    border-bottom: 1px solid var(--line);
  }
  dl > div:last-child {
    border-bottom: none;
  }
  dt {
    color: var(--text-dim);
  }
  dd {
    margin: 0;
    text-align: right;
  }
  dd.warn {
    color: var(--warn);
  }

  @media (min-width: 640px) {
    .facts dl {
      grid-template-columns: 1fr 1fr;
    }
  }

  .weather,
  .running {
    color: var(--ok, #7ad678);
    font-weight: 600;
  }

  .launch {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.5rem;
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .weather .tag,
  .launch .tag {
    background: var(--accent-soft);
    color: var(--accent);
    font-weight: 600;
    font-size: 0.78rem;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
  }
  .weather .why,
  .launch .why {
    max-width: 62ch;
  }
  .launch .soft {
    opacity: 0.7;
  }

  .note.flagged {
    border-left: 3px solid var(--warn);
    padding-left: 0.75rem;
    color: var(--warn);
  }

  .note {
    margin: 0;
    padding: 0.6rem 0.9rem;
    font-size: 0.85rem;
    color: var(--text-dim);
    background: var(--bg-sunken);
    border-left: 3px solid var(--warn);
    border-radius: var(--radius-sm);
  }

  .disclaimer {
    margin: 0.5rem 0 2rem;
    font-size: 0.8rem;
    color: var(--text-faint);
    line-height: 1.5;
  }
</style>
